import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import {
  styled,
  useTheme as useMuiTheme
} from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Storage as StorageIcon,
  PriceChange as PriceChangeIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, THEME_OPTIONS } from '../contexts/ThemeContext';

const drawerWidth = 240;
const collapsedWidth = 78;
const mobileDrawerWidth = 260;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: collapsedWidth,
    ...(open && {
      marginLeft: 0
    })
  })
);

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile'
})(({ theme, open, isMobile }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(isMobile
    ? {
      width: '100%',
      marginLeft: 0,
      zIndex: theme.zIndex.drawer + 1
    }
    : {
      marginLeft: collapsedWidth,
      width: `calc(100% - ${collapsedWidth}px)`,
      ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        // marginLeft: drawerWidth,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        })
      })
    })
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
  minHeight: 64
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme, isactive }) => ({
  minWidth: 40,
  width: 40,
  height: 40,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  color:
    isactive === 'true'
      ? theme.palette.mode === 'dark'
        ? theme.palette.primary.main
        : 'white'
      : 'inherit',
  '& .MuiSvgIcon-root': {
    fontSize: 24,
    width: 24,
    height: 24
  }
}));

const StyledListItemButton = styled(ListItemButton)(
  ({ theme, isactive }) => ({
    height: 48,
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.5, 1),
    padding: theme.spacing(1, 1),
    justifyContent: 'flex-start',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      '& .MuiListItemIcon-root': {
        color: 'white'
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.dark
      }
    },
    '&:hover': {
      backgroundColor:
        isactive === 'true'
          ? theme.palette.primary.dark
          : theme.palette.action.hover
    }
  })
);

const StyledListItemText = styled(ListItemText)(
  ({ theme, isopen, isactive }) => ({
    opacity: isopen === 'true' ? 1 : 0,
    display: 'block',
    visibility: isopen === 'true' ? 'visible' : 'hidden',
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    transition: theme.transitions.create(['opacity', 'visibility'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard
    }),
    '& .MuiTypography-root': {
      fontWeight: isactive === 'true' ? 600 : 500,
      color: isactive === 'true' ? 'inherit' : 'inherit'
    }
  })
);

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Shopify Config', icon: <SettingsIcon />, path: '/shopify-config' },
  { text: 'ERP Config', icon: <StorageIcon />, path: '/erp-config' },
  { text: 'Product Sync', icon: <ShoppingCartIcon />, path: '/product-sync' },
  { text: 'Customer Sync', icon: <PeopleIcon />, path: '/customer-sync' },
  { text: 'Price Sync', icon: <PriceChangeIcon />, path: '/pricing-sync' },
  { text: 'Order Sync', icon: <ShoppingCartIcon />, path: '/order-sync' },
  { text: 'Tracking Sync', icon: <LocalShippingIcon />, path: '/tracking-sync' },
  { text: 'Sync Logs', icon: <AssignmentIcon />, path: '/sync-logs' },
  { text: 'Schedulers', icon: <ScheduleIcon />, path: '/schedulers' },
  { text: 'Users', icon: <PeopleIcon />, path: '/users', adminOnly: true }
];

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile'
})(({ theme, open, isMobile }) => ({
  width: isMobile ? 0 : open ? drawerWidth : collapsedWidth,
  flexShrink: 0,
  position: 'fixed',
  '& .MuiDrawer-paper': {
    width: isMobile ? mobileDrawerWidth : open ? drawerWidth : collapsedWidth,
    boxSizing: 'border-box',
    overflowX: 'hidden',
    borderRight: 'none',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard
    })
  }
}));

export default function DashboardLayout() {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, changeTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  const handleDrawerOpen = () => {
    if (isMobile) {
      setMobileOpen(true);
    } else {
      setOpen(true);
    }
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      setMobileOpen(false);
    } else {
      setOpen(false);
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  const toggleTheme = () => {
    if (isDarkMode) {
      changeTheme(THEME_OPTIONS.LIGHT);
    } else {
      changeTheme(THEME_OPTIONS.DARK);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <AppBarStyled position='fixed' open={open} isMobile={isMobile}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color='inherit'
              aria-label='toggle drawer'
              onClick={() => setMobileOpen((prev) => !prev)}
              edge='start'
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant='h6' noWrap component='div' sx={{ flexGrow: 1 }}>
            Preiser (Shopify - ERP Sync)
          </Typography>
          <Tooltip
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <IconButton
              onClick={toggleTheme}
              size='small'
              color='inherit'
              sx={{ mr: 1 }}
            >
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title='Account settings'>
            <IconButton
              onClick={handleProfileMenuOpen}
              size='small'
              sx={{ ml: 1 }}
              aria-controls={anchorEl ? 'account-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={anchorEl ? 'true' : undefined}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            id='account-menu'
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => handleNavigate('/profile')}>
              <ListItemIcon>
                <PersonIcon fontSize='small' />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize='small' />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBarStyled>

      {/* Mobile drawer */}
      {isMobile ? (
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: mobileDrawerWidth,
              boxSizing: 'border-box',
              boxShadow: (muiTheme) => muiTheme.shadows[2],
              backgroundColor: (muiTheme) => muiTheme.palette.background.paper
            }
          }}
        >
          <DrawerHeader
            sx={{
              height: 64,
              minHeight: 64,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: (muiTheme) => muiTheme.spacing(0, 1),
              borderBottom: (muiTheme) =>
                `1px solid ${muiTheme.palette.divider}`
            }}
          >
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeftIcon />
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List
            sx={{
              mt: 0,
              py: 1,
              '& .MuiListItem-root': {
                width: '100%'
              }
            }}
          >
            {menuItems.map((item) => {
              if (item.adminOnly && user?.role !== 'admin') {
                return null;
              }

              const isActive = location.pathname.includes(item.path);

              return (
                <ListItem
                  key={item.text}
                  disablePadding
                  sx={{
                    display: 'block',
                    py: 0,
                    height: 56 // Fixed height for list items
                  }}
                >
                  <StyledListItemButton
                    onClick={() => {
                      handleNavigate(item.path);
                      handleDrawerClose();
                    }}
                    selected={isActive}
                    isactive={isActive.toString()}
                    isopen='true'
                  >
                    <StyledListItemIcon isactive={isActive.toString()}>
                      {item.icon}
                    </StyledListItemIcon>
                    <StyledListItemText
                      primary={item.text}
                      isopen='true'
                      isactive={isActive.toString()}
                    />
                  </StyledListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Drawer>
      ) : (
        <StyledDrawer
          variant='permanent'
          anchor='left'
          open={open}
          isMobile={isMobile}
        >
          <DrawerHeader
            sx={{
              height: 64,
              minHeight: 64,
              display: 'flex',
              justifyContent: open ? 'flex-end' : 'center',
              alignItems: 'center',
              padding: (muiTheme) => muiTheme.spacing(0, 1),
              borderBottom: (muiTheme) =>
                `1px solid ${muiTheme.palette.divider}`
            }}
          >
            {open ? (
              <IconButton
                onClick={handleDrawerClose}
                sx={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <ChevronLeftIcon
                  sx={{
                    fontSize: 24,
                    width: 24,
                    height: 24
                  }}
                />
              </IconButton>
            ) : (
              <IconButton
                color='inherit'
                aria-label='open drawer'
                onClick={handleDrawerOpen}
                sx={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <MenuIcon
                  sx={{
                    fontSize: 24,
                    width: 24,
                    height: 24
                  }}
                />
              </IconButton>
            )}
          </DrawerHeader>
          <Divider />
          <List
            sx={{
              mt: 0,
              py: 1,
              '& .MuiListItem-root': {
                width: '100%'
              }
            }}
          >
            {menuItems.map((item) => {
              if (item.adminOnly && user?.role !== 'admin') {
                return null;
              }

              const isActive = location.pathname.includes(item.path);

              return (
                <ListItem
                  key={item.text}
                  disablePadding
                  sx={{
                    display: 'block',
                    py: 0,
                    height: 56 // Fixed height for list items
                  }}
                >
                  <StyledListItemButton
                    onClick={() => handleNavigate(item.path)}
                    selected={isActive}
                    isactive={isActive.toString()}
                    isopen={open.toString()}
                  >
                    <StyledListItemIcon isactive={isActive.toString()}>
                      {item.icon}
                    </StyledListItemIcon>
                    <StyledListItemText
                      primary={item.text}
                      isopen={open.toString()}
                      isactive={isActive.toString()}
                    />
                  </StyledListItemButton>
                </ListItem>
              );
            })}
          </List>
        </StyledDrawer>
      )}

      <Box
        component='main'
        sx={{
          flexGrow: 1,
          p: 0,
          pr: 0,
          width: {
            xs: '100%',
            md: `calc(100% - ${open ? drawerWidth : collapsedWidth}px)`
          },
          ml: { xs: 0, md: `${open ? drawerWidth : collapsedWidth}px` },
          transition: (muiTheme) =>
            muiTheme.transitions.create(['margin', 'width'], {
              easing: muiTheme.transitions.easing.sharp,
              duration: muiTheme.transitions.duration.enteringScreen
            }),
          overflowX: 'hidden'
        }}
      >
        <DrawerHeader />
        <Box
          sx={{
            height: 'calc(100% - 64px)',
            overflow: 'auto',
            p: { xs: 2, sm: 3 } // Smaller padding on mobile devices
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
